import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Container, Form, Card, Modal } from "react-bootstrap";
import { BiFullscreen } from "react-icons/bi";
import axios from "axios";

const TimetableForm = ({ school_id, academic_year_id, classList = [], sectionList = [] }) => {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // ------------ State ------------
  const [isLoading, setIsLoading] = useState(false);
  const [timetable, setTimetable] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState(classList);
  const [sections, setSections] = useState(sectionList);
  const [filteredSections, setFilteredSections] = useState([]);
  const [weekday, setWeekday] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [isGridVisible, setIsGridVisible] = useState(false);
  const [showExpand, setShowExpand] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState(0);
  const [isTeacherSelected, setIsTeacherSelected] = useState(false);
  const [viewMode, setViewMode] = useState("class");

  const [filter, setFilter] = useState({
    academic_year_id: academic_year_id || 0,
    school_id: school_id || 0,
    class_id: 0,
    section_id: 0,
  });

  // Guard to ignore stale async responses & prevent overlapping
  const reqIdRef = useRef(0);

  // ------------ One-time bootstraps ------------
  // 1) Weekdays: fetch exactly once
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.post(`${baseUrl}/weekday/`, { action: "READ" });
        if (!cancel) {
          setWeekday((res.data || []).filter(d => d.week_day_name !== "Sunday"));
        }
      } catch (e) {
        if (!cancel) setWeekday([]);
      }
    })();
    return () => { cancel = true; };
  }, [baseUrl]);

  // 2) Teachers: fetch once
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const response = await axios.post(`${baseUrl}/employees/`, {
          action: "READ",
          school_id,
        });
        if (!cancel) setTeachers(response.data || []);
      } catch {
        if (!cancel) setTeachers([]);
      }
    })();
    return () => { cancel = true; };
  }, [baseUrl, school_id]);

  // 3) Classes/Sections from props (don’t refetch if provided)
  useEffect(() => setClasses(classList), [classList]);
  useEffect(() => setSections(sectionList), [sectionList]);

  // 4) Auto-select first active class/section on mount if available (class view only)
  useEffect(() => {
    if (viewMode !== "class" || !school_id || !academic_year_id) return;
    if (!classes?.length || !sections?.length || selectedTeacherId) return;

    const firstActiveClass = classes.find(c => c?.is_active === "Active");
    if (!firstActiveClass) return;

    const relatedSections = sections
      .filter(s => s.class_id === firstActiveClass.class_id && s?.is_active === "Active");
    const firstActiveSection = relatedSections[0];
    if (!firstActiveSection) return;

    setFilteredSections(relatedSections);
    setFilter(prev => ({
      ...prev,
      class_id: firstActiveClass.class_id,
      section_id: firstActiveSection.section_id,
    }));
    // grid visibility will be turned on after the combined fetch below
  }, [viewMode, classes, sections, selectedTeacherId, school_id, academic_year_id]);

  // Keep filteredSections in sync when class changes (class mode)
  useEffect(() => {
    if (viewMode !== "class") return;
    if (!filter.class_id || !sections?.length || selectedTeacherId) {
      setFilteredSections([]);
      return;
    }
    const activeForClass = sections.filter(
      (s) => s.class_id === filter.class_id && s.is_active === "Active"
    );
    setFilteredSections(activeForClass);

    // Auto-pick top section if current is invalid
    if (activeForClass.length && !activeForClass.find(s => s.section_id === filter.section_id)) {
      setFilter(prev => ({ ...prev, section_id: activeForClass[0].section_id }));
    }
  }, [viewMode, filter.class_id, filter.section_id, sections, selectedTeacherId]);

  // ------------ Fetch helpers (single-run chain) ------------
  const fetchClassTimetableBundle = useCallback(
    async (clsId, secId) => {
      // bump request id
      const myId = ++reqIdRef.current;
      setIsLoading(true);
      try {
        // 1. periods & subjects together
        const [periodRes, subjectRes] = await Promise.all([
          axios.post(`${baseUrl}/periodbasedclass/`, {
            action: "READ",
            class_id: clsId,
            school_id,
          }),
          axios.post(`${baseUrl}/teacherssubjectsmap/`, {
            action: "FILTER",
            school_id,
            class_id: clsId,
            section_id: secId,
            academic_year_id,
          }),
        ]);

        if (myId !== reqIdRef.current) return; // stale

        const periodsData = periodRes.data || [];
        const subjectsData = subjectRes.data || [];
        setPeriods(periodsData);
        setSubjects(subjectsData);

        // 2. timetable (using subjectsData for name join)
        const ttRes = await axios.post(`${baseUrl}/timetable/`, {
          action: "FILTER",
          academic_year_id,
          class_id: clsId,
          section_id: secId,
          school_id,
        });

        if (myId !== reqIdRef.current) return; // stale

        const data = (ttRes.data || []).map(item => ({
          ...item,
          subject_name:
            subjectsData.find(s => s.subject_id === item.subject_id)?.subject_name || "",
        }));
        setTimetable(data);
        setIsGridVisible(true);
      } catch (e) {
        if (myId === reqIdRef.current) {
          setPeriods([]);
          setSubjects([]);
          setTimetable([]);
          setIsGridVisible(false);
        }
      } finally {
        if (myId === reqIdRef.current) setIsLoading(false);
      }
    },
    [baseUrl, school_id, academic_year_id]
  );

  const fetchTeacherTimetable = useCallback(
    async (teacherId) => {
      const myId = ++reqIdRef.current;
      setIsLoading(true);
      try {
        // teacher timetable includes periods; weekdays already loaded globally
        const ttRes = await axios.post(`${baseUrl}/teachertimetable/`, {
          action: "READ",
          school_id: school_id || 0,
          teacher_id: teacherId,
        });

        if (myId !== reqIdRef.current) return;

        const timetableData = Array.isArray(ttRes.data) ? ttRes.data : (ttRes.data?.data || []);
        setTimetable(timetableData);

        const uniquePeriods = [...new Map(
          timetableData.map(item => [item.period_order, {
            period_order: item.period_order,
            period_name: item.period_name,
            start_time: item.start_time,
            end_time: item.end_time
          }])
        ).values()].sort((a, b) => a.period_order - b.period_order);

        setPeriods(uniquePeriods);
        setIsGridVisible(true);
      } catch (e) {
        if (myId === reqIdRef.current) {
          setTimetable([]);
          setPeriods([]);
          setIsGridVisible(false);
        }
      } finally {
        if (myId === reqIdRef.current) setIsLoading(false);
      }
    },
    [baseUrl, school_id]
  );

  // ------------ Drive fetching with minimal effects ------------
  // Single effect for CLASS view changes (no subject->timetable double chain)
  useEffect(() => {
    if (viewMode !== "class") return;
    if (selectedTeacherId) return;

    const { class_id, section_id } = filter;
    if (school_id > 0 && academic_year_id > 0 && class_id > 0 && section_id > 0) {
      fetchClassTimetableBundle(class_id, section_id);
    } else {
      // reset when incomplete
      setIsGridVisible(false);
      setPeriods([]);
      setSubjects([]);
      setTimetable([]);
    }
  }, [viewMode, selectedTeacherId, filter.class_id, filter.section_id, school_id, academic_year_id, fetchClassTimetableBundle]);

  // Single effect for TEACHER view changes
  useEffect(() => {
    if (viewMode !== "teacher") return;
    if (selectedTeacherId > 0) {
      fetchTeacherTimetable(selectedTeacherId);
    } else {
      setIsGridVisible(false);
      setTimetable([]);
      setPeriods([]);
    }
  }, [viewMode, selectedTeacherId, fetchTeacherTimetable]);

  // ------------ Helpers ------------
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
  };

  const formatTime12Hour = (time) => {
    if (!time) return "Invalid";
    if (/AM|PM/.test(time)) return time;
    const [h, m] = time.split(":").map(Number);
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${m.toString().padStart(2, "0")} ${suffix}`;
    };

  // Memoize the grid so renders don’t recompute unnecessarily
  const timetableGrid = useMemo(() => {
    const rows = periods?.length || 0;
    const grid = Array.from({ length: rows }, () => Array(6).fill(null));
    if (!Array.isArray(timetable) || !rows) return grid;

    for (const entry of timetable) {
      const {
        period_order,
        week_day_id,
        subject_name,
        teacher_name,
        class_name,
        section_name
      } = entry;

      if (
        Number.isInteger(period_order) &&
        period_order >= 1 &&
        period_order <= rows &&
        Number.isInteger(week_day_id) &&
        week_day_id >= 1 &&
        week_day_id <= 6
      ) {
        grid[period_order - 1][week_day_id - 1] = isTeacherSelected
          ? { display1: class_name || "-", display2: section_name ? `(${section_name})` : "" }
          : { display1: subject_name || "-", display2: teacher_name || "" };
      }
    }
    return grid;
  }, [periods, timetable, isTeacherSelected]);

  // ------------ Renderers ------------
  const renderTimetableGrid = () => (
    <div
      className="timetableDivGrid p-2"
      style={{
        overflowX: "auto",
        overflowY: "auto",
        maxHeight: "360px",
        border: "1px solid #dee2e6",
        borderRadius: "4px",
        backgroundColor: "#fff",
      }}
    >
      <div
        className="grid"
        style={{
          display: "grid",
          gridAutoColumns: "minmax(100px, auto)",
          gridTemplateColumns: `120px repeat(${periods.length}, 150px)`,
          minWidth: `${150 * (periods.length + 1)}px`,
        }}
      >
        {/* Header Row */}
        <div
          style={{
            fontWeight: "bold",
            border: "1px solid #dee2e6",
            padding: "4px",
            textAlign: "center",
            backgroundColor: "#f1f1f1",
            position: "sticky",
            left: 0,
            zIndex: 2,
          }}
        >
          Day / Period
        </div>

        {periods.map((p, i) => (
          <div
            key={`header-${i}`}
            style={{
              fontWeight: "bold",
              border: "1px solid #dee2e6",
              padding: "4px",
              fontSize: "12px",
              textAlign: "center",
              backgroundColor: "#f1f1f1",
            }}
          >
            {p.period_name || `Period ${i + 1}`}
            <br />
            {p.start_time && p.end_time && (
              <span style={{ fontSize: "10px", color: "#666" }}>
                {formatTime12Hour(p.start_time)} - {formatTime12Hour(p.end_time)}
              </span>
            )}
          </div>
        ))}

        {/* Body Rows */}
        {weekday.map((day) => (
          <React.Fragment key={day.week_day_id}>
            <div
              style={{
                border: "1px solid #dee2e6",
                padding: "4px",
                backgroundColor: "#f8f9fa",
                textAlign: "center",
                position: "sticky",
                left: 0,
                zIndex: 1,
              }}
            >
              {day.week_day_name}
            </div>

            {periods.map((_, pIdx) => {
              const entry = timetableGrid[pIdx]?.[day.week_day_id - 1];
              return (
                <div
                  key={`cell-${day.week_day_id}-${pIdx}`}
                  style={{
                    border: "1px solid #dee2e6",
                    fontSize: "12px",
                    textAlign: "center",
                    backgroundColor: entry?.display1 ? "white" : "#f9f9f9",
                    minHeight: "48px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    padding: "2px",
                  }}
                >
                  {entry?.display1 ? (
                    <>
                      <div>{entry.display1}</div>
                      {entry.display2 && (
                        <div style={{ fontSize: "11px", color: "#6c757d" }}>{entry.display2}</div>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderDropdowns = () => (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {/* View Mode */}
      <Form.Select
        size="sm"
        value={viewMode}
        onChange={(e) => {
          const mode = e.target.value;
          setViewMode(mode);
          // reset everything safely
          setSelectedTeacherId(0);
          setIsTeacherSelected(false);
          setFilter(prev => ({ ...prev, class_id: 0, section_id: 0 }));
          setFilteredSections([]);
          setPeriods([]);
          setTimetable([]);
          setIsGridVisible(false);
          ++reqIdRef.current; // invalidate any inflight requests
        }}
        style={{ width: "150px" }}
      >
        <option value="class">By Class</option>
        <option value="teacher">By Teacher</option>
      </Form.Select>

      {/* Class mode */}
      {viewMode === "class" && (
        <>
          <Form.Select
            size="sm"
            name="class_id"
            value={filter.class_id}
            onChange={handleFilterChange}
            style={{ width: "100px" }}
          >
            <option value={0}>Class</option>
            {classes.filter(c => c.is_active === "Active").map(cls => (
              <option key={cls.class_id} value={cls.class_id}>{cls.class_name}</option>
            ))}
          </Form.Select>

          <Form.Select
            size="sm"
            name="section_id"
            value={filter.section_id}
            onChange={handleFilterChange}
            disabled={!filter.class_id}
            style={{ width: "100px" }}
          >
            <option value={0}>Section</option>
            {filteredSections.filter(s => s.is_active === "Active").map(section => (
              <option key={section.section_id} value={section.section_id}>{section.section_name}</option>
            ))}
          </Form.Select>
        </>
      )}

      {/* Teacher mode */}
      {viewMode === "teacher" && (
        <Form.Select
          size="sm"
          name="teacher_id"
          value={selectedTeacherId}
          onChange={(e) => {
            const tid = parseInt(e.target.value, 10);
            setSelectedTeacherId(tid);
            setIsTeacherSelected(tid > 0);
          }}
          style={{ width: "200px" }}
        >
          <option value={0}>Select Teacher</option>
          {teachers
            // .filter(t => t.role_name === "Teacher" && t.status === "Active")
            .map(t => (
              <option key={t.emp_id} value={t.emp_id}>
                {t.emp_name} 
              </option>
            ))}
        </Form.Select>
      )}

      <button
        type="button"
        title="Expand Report"
        className="btn btn-outline-secondary btn-sm"
        onClick={() => setShowExpand(true)}
      >
        <BiFullscreen />
      </button>
    </div>
  );

  return (
    <Container fluid>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="mb-0">Time Table</h5>
          {renderDropdowns()}
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          {!isGridVisible ? (
            <p className="text-center mt-3">
              {viewMode === "teacher" ? "Select a teacher to view timetable" : "Select class and section to view the timetable"}
            </p>
          ) : renderTimetableGrid()}
        </Card.Body>
      </Card>

      <Modal show={showExpand} onHide={() => setShowExpand(false)} size="xl" backdrop="static" >
        <Modal.Header closeButton>
          <Modal.Title className="w-100 d-flex justify-content-between align-items-center">
            <span>Expanded Timetable</span>
            {renderDropdowns()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!isGridVisible ? (
            <p className="text-center mt-3">
              {viewMode === "teacher" ? "Select a teacher to view timetable" : "Select class and section to view the timetable"}
            </p>
          ) : renderTimetableGrid()}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default TimetableForm;
